import { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import _ from 'lodash';
import { ReviewPayload, Review } from '../../schema';
import { editReviewAtom, listLoadReviewAtom } from '../../atoms/review';
import { defaultAuth } from '../../atoms/auth';
import { Loading } from '../../widgets';
import {
  notificationAtom,
  NotificationType,
  pageLoadingAtom,
} from '../../atoms/system';
import { useAtom, useSetAtom } from 'jotai';
import produce from 'immer';
import { Image, IResponse } from '../../types';
import api, { uploadImages } from '../../api';
import FormReview from './Form.Review';
import { useImmer } from 'use-immer';
import cuid from 'cuid';
import { IReview } from '../../inter/detail';

const Edit = (): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const setListLoad = useSetAtom(listLoadReviewAtom);
  const form = useForm<ReviewPayload>({
    resolver: zodResolver(Review),
  });
  const [showEditReview, setShowEditReview] = useAtom(editReviewAtom);
  const [loading, setLoading] = useImmer<boolean>(false);
  const [imageList, setImageList] = useImmer<Image[]>([]);
  const onSubmit = async (data: ReviewPayload) => {
    try {
      pageLoading(true);
      const images = await uploadImages(
        _.toNumber(defaultAuth?.insider_id),
        imageList,
      );

      const payload = produce(data, draft => {
        if (!_.isUndefined(images)) {
          draft.images = images;
        }
      });
      await api.post(`api/reviews/${showEditReview.comment_id}`, {
        json: payload,
      });
      setListLoad(cuid());
      setShowEditReview(
        produce(draft => {
          draft.show = false;
          draft.comment_id = '';
        }),
      );
      notification({
        show: true,
        type: NotificationType.success,
        title: 'Cập nhật thành công',
      });
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  useEffect(() => {
    if (showEditReview.comment_id.length > 0 && showEditReview.show) {
      (async () => {
        try {
          setLoading(true);
          const result = await api
            .get(`api/reviews/${showEditReview.comment_id}`)
            .json<IResponse<IReview | null>>();
          if (_.isNull(result.data)) {
            throw Error();
          }
          form.reset({
            target_id: result.data.pageTargetId,
            status: result.data.status,
            amount: _.toNumber(result.data.amount),
            title: _.toString(result.data.title),
            desc: result.data.desc,
            youtube: result.data.youtube,
            name: result.data.customer.name,
            email: result.data.customer.email,
            phone: result.data.customer.phone,
            images: [],
          });
          setImageList(result.data.images);
          setLoading(false);
        } catch (e) {
          console.log(e);
          setShowEditReview(
            produce(draft => {
              draft.show = false;
              draft.comment_id = '';
            }),
          );
          notification({
            show: true,
            type: null,
            title: null,
          });
        }
      })();
    }
  }, [showEditReview.comment_id]);
  return (
    <>
      <Transition show={showEditReview.show} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() =>
            setShowEditReview(
              produce(draft => {
                draft.show = false;
                draft.comment_id = '';
              }),
            )
          }
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-end p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden p-4 rounded-xl bg-white shadow-xl transition-all">
                  {loading ? (
                    <div className="flex justify-center items-center min-h-screen">
                      <Loading color="indigo-600" size={6} />
                    </div>
                  ) : (
                    <FormProvider {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="w-full">
                          <h3 className="text-lg font-medium text-gray-900">
                            Chỉnh sửa đánh giá
                          </h3>
                          <FormReview
                            imageProps={{
                              list: imageList,
                              action: setImageList,
                            }}
                            disabledFields={{
                              target_id: showEditReview.target_title,
                            }}
                          />
                          <div className="flex justify-between w-full mt-6">
                            <button
                              onClick={() => {
                                setShowEditReview(
                                  produce(draft => {
                                    draft.show = false;
                                    draft.comment_id = '';
                                  }),
                                );
                              }}
                              type="button"
                              className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            >
                              Hủy
                            </button>
                            <button
                              type="submit"
                              className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              Cập nhật đánh giá
                            </button>
                          </div>
                        </div>
                      </form>
                    </FormProvider>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Edit;
